import { Pill } from '@/components/shared/Pill';
import { CircularProgress, Menu, MenuItem } from '@mui/material';
import { useRef, useState } from 'react';
import { PillStatus } from '@/components/shared/Pill/types';
import { iconSizeConfig } from '@/components/shared/Pill/constants';

export const PillMenu = ({
  options,
  initialValue,
  loading = false,
  loadingLabel = 'Updating...',
  onChange = () => {},
  readOnly = false,
  getPillStatus = () => 'info',
  menuAnchorOrigin = { vertical: 'bottom', horizontal: 'right' },
  menuTransformOrigin = { vertical: 'top', horizontal: 'right' },
  size = 'small',
  disabled = false,
  label = '',
  className = '',
}: {
  options: string[];
  initialValue: string;
  loading: boolean;
  loadingLabel: string;
  onChange: (status: string) => void;
  readOnly?: boolean;
  getPillStatus?: (status: string) => PillStatus;
  menuAnchorOrigin?: { vertical: 'top' | 'bottom'; horizontal: 'left' | 'right' };
  menuTransformOrigin?: { vertical: 'top' | 'bottom'; horizontal: 'left' | 'right' };
  size?: 'small' | 'medium' | 'large';
  disabled?: boolean;
  label?: string;
  className?: string;
}) => {
  const [status, setStatus] = useState<string>(initialValue);
  const [anchorElement, setAnchorElement] = useState<HTMLElement | undefined>(undefined);
  const mainRef = useRef(null);
  const handleMenuClick = (event: React.MouseEvent) => {
    event.stopPropagation();
    if (disabled) return;
    setAnchorElement(mainRef.current ?? undefined);
  };

  const handleClose = (event: React.MouseEvent) => {
    event.stopPropagation();
    setAnchorElement(undefined);
  };

  const handleChange = (event: React.MouseEvent, status: string) => {
    event.stopPropagation();
    if (disabled) return;
    setStatus(status);
    onChange(status);
    handleClose(event);
  };

  return (
    <>
      <div ref={mainRef} data-testid="pill-menu">
        <Pill
          disableRipple
          label={loading ? loadingLabel : label || status}
          showDropdownIcon={!loading && !readOnly}
          type={readOnly ? 'readonly' : 'clickable'}
          onDropdownClick={handleMenuClick}
          onClick={handleMenuClick}
          status={getPillStatus(status)}
          showIcon={loading}
          icon={
            loading ? <CircularProgress size={`calc(${iconSizeConfig[size]} * 0.8)`} /> : undefined
          }
          state={loading || disabled ? 'pressed' : 'default'}
          disabled={loading || disabled}
          size={size}
          className={className}
        />
      </div>
      <Menu
        elevation={0}
        anchorEl={anchorElement}
        open={Boolean(anchorElement)}
        onClose={handleClose}
        anchorOrigin={{
          vertical: menuAnchorOrigin.vertical,
          horizontal: menuAnchorOrigin.horizontal,
        }}
        transformOrigin={{
          vertical: menuTransformOrigin.vertical,
          horizontal: menuTransformOrigin.horizontal,
        }}
        sx={{
          '& .MuiMenu-paper': {
            marginBottom: menuTransformOrigin.vertical === 'bottom' ? '1rem' : '0',
            marginTop: menuTransformOrigin.vertical === 'top' ? '1rem' : '0',
          },
        }}
      >
        {options.map((status) => (
          <MenuItem key={status} onClick={(e) => handleChange(e, status)}>
            {status}
          </MenuItem>
        ))}
      </Menu>
    </>
  );
};
